export const REJECTED_INVALID_DATA_MESSAGE = 'Rejected - Invalid data';
export const NOT_PROCESSED_MESSAGE = 'Not processed';
// Worded like the import's own checks, which never get to report this one themselves.
export const INVALID_ORG_ID_MESSAGE = 'Found invalid org_id';

const unwrapCell = value => {
  if (value == null || value === '') {
    return '';
  }

  if (typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'result')) {
      return unwrapCell(value.result);
    }

    if (value.text != null) {
      return String(value.text);
    }

    if (Array.isArray(value.richText)) {
      return value.richText.map(part => part.text).join('');
    }
  }

  return value;
};

export const normalizeImportCell = value => String(unwrapCell(value)).trim();

export const normalizeImportAmount = value => {
  const unwrapped = unwrapCell(value);

  if (unwrapped == null || unwrapped === '') {
    return '';
  }

  const numericValue = Number(String(unwrapped).replace(/,/g, '').trim());

  return Number.isFinite(numericValue) ? String(numericValue) : String(unwrapped).trim();
};

export const getUploadedAccountDataRowKey = row =>
  [
    normalizeImportCell(row.orgId),
    normalizeImportCell(row.accountId),
    normalizeImportCell(row.jurisdictionId),
    normalizeImportCell(row.begEndFlag).toUpperCase(),
    normalizeImportAmount(row.amount),
  ].join('|');

// The Aggregate duplicate option sums every spreadsheet row sharing this combination into a single
// imported row, so the amount cannot take part in the match.
export const getUploadedAccountDataRowIdentityKey = row =>
  [
    normalizeImportCell(row.orgId),
    normalizeImportCell(row.accountId),
    normalizeImportCell(row.jurisdictionId),
    normalizeImportCell(row.begEndFlag).toUpperCase(),
  ].join('|');

const EXCEL_HEADER_ROW_INDEX = 1;

const toDisplayRowNumber = excelRowNumber =>
  typeof excelRowNumber === 'number' ? excelRowNumber - EXCEL_HEADER_ROW_INDEX : excelRowNumber;

const toGridFields = row => ({
  // Excel row 1 is the header, so the grid shows data rows starting at 1.
  rowNumber: toDisplayRowNumber(row.rowNumber),
  orgId: normalizeImportCell(row.orgId),
  accountId: normalizeImportCell(row.accountId),
  jurisdictionId: normalizeImportCell(row.jurisdictionId),
  begEndFlag: normalizeImportCell(row.begEndFlag),
  amount: unwrapCell(row.amount),
});

const toStagedGridFields = stagedRow => ({
  // Nothing ties a staged row back to a spreadsheet line, so only matched rows carry a number.
  rowNumber: null,
  orgId: normalizeImportCell(stagedRow.orgId),
  accountId: normalizeImportCell(stagedRow.accountId),
  jurisdictionId: normalizeImportCell(stagedRow.jurisdictionId),
  begEndFlag: normalizeImportCell(stagedRow.begEndFlag),
  amount: unwrapCell(stagedRow.amount),
});

const toSortableRowNumber = rowNumber => rowNumber ?? Number.MAX_SAFE_INTEGER;

const byFailuresThenRowNumber = (left, right) => {
  if (left.importStatus !== right.importStatus) {
    return left.importStatus === 'Failed' ? -1 : 1;
  }

  return toSortableRowNumber(left.rowNumber) - toSortableRowNumber(right.rowNumber);
};

const getDroppedRowMessage = (fileRow, invalidOrgIds, fallbackMessage) =>
  invalidOrgIds.has(normalizeImportCell(fileRow.orgId)) ? INVALID_ORG_ID_MESSAGE : fallbackMessage;

const groupRowsByKey = rows =>
  rows.reduce((grouped, row) => {
    const key = getUploadedAccountDataRowKey(row);
    const matchingRows = grouped.get(key);

    if (matchingRows) {
      matchingRows.push(row);
    } else {
      grouped.set(key, [row]);
    }

    return grouped;
  }, new Map());

const toMatchedStagedGridRow = (fileRow, stagedRow) => {
  const errorMessage = stagedRow.errorMessage || null;

  return {
    ...toGridFields(fileRow),
    importStatus: errorMessage ? 'Failed' : 'Success',
    errorMessage,
  };
};

const toUnstagedGridRow = (fileRow, invalidOrgIds) => ({
  ...toGridFields(fileRow),
  importStatus: 'Failed',
  errorMessage: getDroppedRowMessage(fileRow, invalidOrgIds, NOT_PROCESSED_MESSAGE),
});

const matchStagedRowsByAmount = ({ fileRows, stagedRows, invalidOrgIds }) => {
  const remainingStagedRows = groupRowsByKey(stagedRows);
  let matchedStagedRowCount = 0;

  const gridRows = fileRows.map(fileRow => {
    const stagedRow = remainingStagedRows.get(getUploadedAccountDataRowKey(fileRow))?.shift();

    if (!stagedRow) {
      return toUnstagedGridRow(fileRow, invalidOrgIds);
    }

    matchedStagedRowCount += 1;

    return toMatchedStagedGridRow(fileRow, stagedRow);
  });

  return {
    gridRows,
    matchedStagedRowCount,
    unmatchedStagedRows: [...remainingStagedRows.values()].flat(),
  };
};

// Aggregation collapses duplicates before staging, so one staged row speaks for every spreadsheet
// row sharing its combination rather than being consumed by the first of them.
const matchStagedRowsByIdentity = ({ fileRows, stagedRows, invalidOrgIds }) => {
  const stagedRowsByIdentity = new Map();

  stagedRows.forEach(stagedRow => {
    const key = getUploadedAccountDataRowIdentityKey(stagedRow);

    if (!stagedRowsByIdentity.has(key)) {
      stagedRowsByIdentity.set(key, stagedRow);
    }
  });

  const matchedIdentities = new Set();

  const gridRows = fileRows.map(fileRow => {
    const key = getUploadedAccountDataRowIdentityKey(fileRow);
    const stagedRow = stagedRowsByIdentity.get(key);

    if (!stagedRow) {
      return toUnstagedGridRow(fileRow, invalidOrgIds);
    }

    matchedIdentities.add(key);

    return toMatchedStagedGridRow(fileRow, stagedRow);
  });

  return {
    gridRows,
    matchedStagedRowCount: matchedIdentities.size,
    unmatchedStagedRows: stagedRows.filter(
      stagedRow => !matchedIdentities.has(getUploadedAccountDataRowIdentityKey(stagedRow)),
    ),
  };
};

/**
 * Compare spreadsheet rows against the rows the failed import staged in t_slt_import_raw.
 * Staged rows keep the error text the database recorded; file rows the import never staged are
 * surfaced explicitly, since the staging table is the only source the failure grid otherwise has.
 * @return {object[] | null} null when no staged row matches the file, meaning the resolved batch
 * belongs to some other upload and the raw server rows should be shown instead.
 */
export const diffFailedUploadAccountDataRows = ({
  fileRows,
  stagedRows,
  invalidOrgIds = new Set(),
  shouldAggregateData = false,
}) => {
  const matchStagedRows = shouldAggregateData ? matchStagedRowsByIdentity : matchStagedRowsByAmount;

  const { gridRows, matchedStagedRowCount, unmatchedStagedRows } = matchStagedRows({
    fileRows,
    stagedRows,
    invalidOrgIds,
  });

  if (stagedRows.length && !matchedStagedRowCount) {
    return null;
  }

  return [
    ...gridRows,
    ...unmatchedStagedRows.map(stagedRow => ({
      ...toStagedGridFields(stagedRow),
      importStatus: stagedRow.errorMessage ? 'Failed' : 'Success',
      errorMessage: stagedRow.errorMessage || null,
    })),
  ].sort(byFailuresThenRowNumber);
};

const toImportedGridRow = fileRow => ({
  ...toGridFields(fileRow),
  importStatus: 'Success',
  errorMessage: null,
});

const toRejectedGridRow = (fileRow, invalidOrgIds) => ({
  ...toGridFields(fileRow),
  importStatus: 'Failed',
  errorMessage: getDroppedRowMessage(fileRow, invalidOrgIds, REJECTED_INVALID_DATA_MESSAGE),
});

// Under the Reject duplicate option each spreadsheet row needs its own imported row, so a second
// row carrying the same amount is a genuine rejection rather than a match.
const matchImportedRowsByAmount = ({ fileRows, importedRows, invalidOrgIds }) => {
  const remainingImportedCounts = new Map();

  importedRows.forEach(importedRow => {
    const key = getUploadedAccountDataRowKey(importedRow);
    remainingImportedCounts.set(key, (remainingImportedCounts.get(key) || 0) + 1);
  });

  return fileRows.map(fileRow => {
    const key = getUploadedAccountDataRowKey(fileRow);
    const remainingCount = remainingImportedCounts.get(key) || 0;

    if (!remainingCount) {
      return toRejectedGridRow(fileRow, invalidOrgIds);
    }

    remainingImportedCounts.set(key, remainingCount - 1);

    return toImportedGridRow(fileRow);
  });
};

// Aggregated duplicates share one imported row whose amount is their sum, so a spreadsheet row is
// only rejected when nothing imported for its combination at all.
const matchImportedRowsByIdentity = ({ fileRows, importedRows, invalidOrgIds }) => {
  const importedIdentities = new Set(importedRows.map(getUploadedAccountDataRowIdentityKey));

  return fileRows.map(fileRow =>
    importedIdentities.has(getUploadedAccountDataRowIdentityKey(fileRow))
      ? toImportedGridRow(fileRow)
      : toRejectedGridRow(fileRow, invalidOrgIds),
  );
};

/**
 * Compare spreadsheet rows (with Excel row numbers) against rows that landed in acc_data_dtl.
 * File rows with no remaining imported match are treated as rejected.
 */
export const diffUploadedAccountDataRows = ({
  fileRows,
  importedRows,
  invalidOrgIds = new Set(),
  shouldAggregateData = false,
}) => {
  const matchImportedRows = shouldAggregateData
    ? matchImportedRowsByIdentity
    : matchImportedRowsByAmount;

  return matchImportedRows({ fileRows, importedRows, invalidOrgIds }).sort(byFailuresThenRowNumber);
};
